import { useEffect, useRef, useState } from 'react';
import { t, useTranslation } from '../i18n';

interface Props {
  peerName: string;
  peerPhotoB64: string | null;
  direction: 'incoming' | 'outgoing';
  state: 'ringing' | 'connecting' | 'connected' | 'ending';
  hasVideo: boolean;
  startedAt: string | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  micMuted: boolean;
  cameraOn: boolean;
  onAccept: () => void;
  onReject: () => void;
  onHangup: () => void;
  onToggleMic: () => void;
  onToggleCamera: () => void;
}

function fmt(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => n.toString().padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
}

export default function CallScreen(props: Props) {
  useTranslation();
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const [duration, setDuration] = useState(0);
  const [outputDevices, setOutputDevices] = useState<MediaDeviceInfo[]>([]);
  const [outputDeviceId, setOutputDeviceId] = useState<string>('default');
  const [showOutputPicker, setShowOutputPicker] = useState(false);
  const [outputSupported, setOutputSupported] = useState(true);

  useEffect(() => {
    if (localVideoRef.current && props.localStream) {
      localVideoRef.current.srcObject = props.localStream;
    }
  }, [props.localStream]);

  useEffect(() => {
    if (remoteVideoRef.current && props.remoteStream) {
      remoteVideoRef.current.srcObject = props.remoteStream;
    }
    if (remoteAudioRef.current && props.remoteStream) {
      remoteAudioRef.current.srcObject = props.remoteStream;
    }
  }, [props.remoteStream]);

  useEffect(() => {
    let cancelled = false;
    async function loadDevices() {
      try {
        if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
          if (!cancelled) setOutputSupported(false);
          return;
        }
        const all = await navigator.mediaDevices.enumerateDevices();
        const outs = all.filter((d) => d.kind === 'audiooutput');
        if (cancelled) return;
        setOutputDevices(outs);
        const audioEl: any = remoteAudioRef.current;
        const videoEl: any = remoteVideoRef.current;
        const supported = (audioEl && typeof audioEl.setSinkId === 'function') || (videoEl && typeof videoEl.setSinkId === 'function');
        setOutputSupported(!!supported);
      } catch (_e) {
        setOutputSupported(false);
      }
    }
    loadDevices();
    if (navigator.mediaDevices && 'addEventListener' in navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener('devicechange', loadDevices);
    }
    return () => {
      cancelled = true;
      if (navigator.mediaDevices && 'removeEventListener' in navigator.mediaDevices) {
        navigator.mediaDevices.removeEventListener('devicechange', loadDevices);
      }
    };
  }, [props.remoteStream]);

  async function applyOutput(deviceId: string) {
    setOutputDeviceId(deviceId);
    setShowOutputPicker(false);
    const audioEl: any = remoteAudioRef.current;
    const videoEl: any = remoteVideoRef.current;
    const targets: any[] = [];
    if (audioEl && typeof audioEl.setSinkId === 'function') targets.push(audioEl);
    if (videoEl && typeof videoEl.setSinkId === 'function') targets.push(videoEl);
    for (const el of targets) {
      try { await el.setSinkId(deviceId); } catch (_e) { /* ignore */ }
    }
  }

  function deviceLabel(d: MediaDeviceInfo): string {
    if (d.deviceId === 'default') return t('call.audioOut.auto');
    if (d.label) return d.label;
    return 'Output ' + d.deviceId.slice(0, 6);
  }

  function deviceIcon(d: MediaDeviceInfo): string {
    const lbl = (d.label || '').toLowerCase();
    if (lbl.includes('bluetooth') || lbl.includes('bt ') || lbl.includes('airpod') || lbl.includes('wireless')) return '🎧';
    if (lbl.includes('earpiece') || lbl.includes('auricular') || lbl.includes('phone') || lbl.includes('communication')) return '📞';
    if (lbl.includes('speaker') || lbl.includes('altavoz')) return '🔊';
    return '🔊';
  }

  useEffect(() => {
    if (props.state !== 'connected' || !props.startedAt) {
      setDuration(0);
      return;
    }
    const start = new Date(props.startedAt).getTime();
    const tick = () => setDuration(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [props.state, props.startedAt]);

  const showRemoteVideo =
    props.hasVideo && props.remoteStream && props.remoteStream.getVideoTracks().some((t2) => t2.enabled);

  return (
    <div className="call-overlay">
      {showRemoteVideo ? (
        <video ref={remoteVideoRef} autoPlay playsInline className="call-remote-video" />
      ) : (
        <div className="call-remote-fallback">
          <div className="call-avatar-big">
            {props.peerPhotoB64 ? (
              <img src={props.peerPhotoB64} alt="" />
            ) : (
              <span>{(props.peerName || '?').slice(0, 1).toUpperCase()}</span>
            )}
          </div>
        </div>
      )}

      <audio ref={remoteAudioRef} autoPlay />

      <div className="call-info">
        <div className="call-name">{props.peerName}</div>
        <div className="call-state">
          {props.state === 'ringing' && props.direction === 'outgoing' && t('call.calling')}
          {props.state === 'ringing' && props.direction === 'incoming' && (props.hasVideo ? t('call.incoming.video') : t('call.incoming.audio'))}
          {props.state === 'connecting' && t('call.connecting')}
          {props.state === 'connected' && fmt(duration)}
          {props.state === 'ending' && t('call.ending')}
        </div>
      </div>

      {props.hasVideo && props.localStream && (
        <video
          ref={localVideoRef}
          autoPlay
          playsInline
          muted
          className={'call-local-video' + (props.cameraOn ? '' : ' camera-off')}
        />
      )}

      <div className="call-controls">
        {props.state === 'ringing' && props.direction === 'incoming' ? (
          <>
            <button className="call-btn reject" onClick={props.onReject} aria-label={t('call.reject')}>
              <span className="call-btn-icon">✕</span>
              <span className="call-btn-label">{t('call.reject')}</span>
            </button>
            <button className="call-btn accept" onClick={props.onAccept} aria-label={t('call.accept')}>
              <span className="call-btn-icon">{props.hasVideo ? '📹' : '📞'}</span>
              <span className="call-btn-label">{t('call.accept')}</span>
            </button>
          </>
        ) : (
          <>
            <button className={`call-btn toggle ${props.micMuted ? 'muted' : ''}`} onClick={props.onToggleMic} aria-label={t('call.mic')}>
              <span className="call-btn-icon">{props.micMuted ? '🔇' : '🎤'}</span>
            </button>
            {props.hasVideo && (
              <button className={`call-btn toggle ${!props.cameraOn ? 'muted' : ''}`} onClick={props.onToggleCamera} aria-label={t('call.camera')}>
                <span className="call-btn-icon">{props.cameraOn ? '📹' : '🚫'}</span>
              </button>
            )}
            {outputSupported && outputDevices.length >= 1 && (
              <div className="call-output-anchor">
                <button
                  className="call-btn toggle"
                  onClick={() => setShowOutputPicker((v) => !v)}
                  aria-label={t('call.audioOut')}
                >
                  <span className="call-btn-icon">🔊</span>
                </button>
                {showOutputPicker && (
                  <div className="call-output-picker" onClick={(e) => e.stopPropagation()}>
                    {outputDevices.map((d) => (
                      <button
                        key={d.deviceId}
                        className={d.deviceId === outputDeviceId ? 'active' : ''}
                        onClick={() => applyOutput(d.deviceId)}
                      >
                        <span className="output-icon">{deviceIcon(d)}</span>
                        <span className="output-label">{deviceLabel(d)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <button className="call-btn hangup" onClick={props.onHangup} aria-label={t('call.hangup')}>
              <span className="call-btn-icon">📞</span>
            </button>
          </>
        )}
      </div>
    </div>
  );
}
